import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getSetFilterParams,
  getSetDatefilterParams,
  getSetCheckboxFilterParams,
  getCommonNumberFilterParams,
} from '../../utils/agGridFilters';
import { isMobile } from '../../utils/deviceUtils';
import DeleteConfirmationModal from '../../shared/modal/deleteConfirmation';
import { DateTimeFormatter, NameFormatter } from '../../shared/controls/formatters';
import { currencyFormatterByField } from '../../shared/constants';

const OptionButton = ({
  handleOpenEditForm,
  paramsValue,
  handleOpenViewForm,
  handleClickOpenConvertLead,
  rowData,
  handleClickDelete,
  emailPopup,
  hasDeleteLeadPermission,
  hasUpdateLeadPermission,
  hasInfoLeadPermission,
}) => {
  // const [isHover, setHover] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const handleDeleteClick = (e) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const handleConfirmDelete = () => {
    console.log('rowData==>', rowData);
    handleClickDelete(rowData);
    setShowConfirmation(false);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };
  return (
    <div className="action-menu ">
      {hasUpdateLeadPermission && (
        <img
          src="/assets/images/edit.png"
          className="action-img1"
          onClick={handleOpenEditForm}
          alt="Edit Lead"
          title="Edit Lead"
        />
      )}
      {hasInfoLeadPermission && (
        <img
          src="/assets/images/view.png"
          className="action-img2"
          onClick={handleOpenViewForm}
          alt="View Lead"
          title="View Lead"
        />
      )}
      <div className="dropdown">
        <span className="dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
          <img
            src="/assets/images/crm/leads/actiondot.svg"
            className="action-img1 dropdown-toggle"
            width="18px"
            alt="More"
            title="More"
          />
        </span>

        <ul className="dropdown-menu">
          {hasUpdateLeadPermission && (
            <li className="dropdown-item">
              <Link to="/leads/editLead" state={rowData}>
                Edit
              </Link>
            </li>
          )}
          <li className="dropdown-item">
            <Link to="/leads/convertLead" state={rowData}>
              Convert
            </Link>
          </li>
          {hasDeleteLeadPermission && (
            <li className="dropdown-item">
              <Link onClick={handleDeleteClick}>Delete</Link>
              <DeleteConfirmationModal
                show={showConfirmation}
                handleClose={handleCloseConfirmation}
                handleConfirm={handleConfirmDelete}
                content={'lead'}
              />
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

const useLeadColumnDefs = ({
  hasUpdateLeadPermission,
  hasInfoLeadPermission,
  hasDeleteLeadPermission,
  emailPopup,
  gridData,
  handleClickDelete,
  leadSourceDropdown,
  industryTypeDropdown,
  campaignListDropdown,
  tagsList,
  salutation,
  revenueTypeDropdown,
  timeZoneList,
  owner,
}) => {
  const navigate = useNavigate();
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const commonNumberFilterParams = useMemo(() => getCommonNumberFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const checkboxFilterParams = useMemo(() => getSetCheckboxFilterParams, []);
  const filterParams = useMemo(() => getSetFilterParams, []);
  const EmailCellRenderer = useCallback(
    (params) => (params.value ? <a href={`mailto:${params.value}`}>{params.value}</a> : null),
    []
  );
  const handleOpenEditForm = (paramsValue) => {
    navigate('/leads/editLead', {
      state: paramsValue,
    });
  };

  const handleOpenViewForm = (paramsValue) => {
    navigate(`/leads/viewLead?id=${paramsValue.id}&pageNo=${gridData?.inputPayload?.pageNumber}`, {
      state: paramsValue,
    });
  };

  const handleClickOpenConvertLead = (paramsValue) => {
    navigate('/leads/convertLead', {
      state: paramsValue,
    });
  };
  const actionsColumn = useMemo(() => {
    if (!(hasUpdateLeadPermission || hasInfoLeadPermission || hasDeleteLeadPermission)) return [];

    return [
      {
        headerName: 'Actions',
        headerCheckboxSelection: (params) => params?.node && !params.node.group,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: (params) => params?.node && !params.node.group,
        cellRenderer: (params) => {
          if (!params?.node || params.node.group) return null;

          const rowData = { ...params.data, rowIndex: params.rowIndex };

          return (
            <OptionButton
              rowData={rowData}
              hasInfoLeadPermission={hasInfoLeadPermission}
              hasDeleteLeadPermission={hasDeleteLeadPermission}
              hasUpdateLeadPermission={hasUpdateLeadPermission}
              handleOpenEditForm={() => handleOpenEditForm(rowData)}
              handleOpenViewForm={() => handleOpenViewForm(rowData)}
              handleClickOpenConvertLead={() => handleClickOpenConvertLead(rowData)}
              handleClickDelete={() => handleClickDelete(rowData)}
              emailPopup={() => emailPopup(rowData)}
            />
          );
        },
        maxWidth: 100,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: 'left',
      },
    ];
  }, [
    hasUpdateLeadPermission,
    hasInfoLeadPermission,
    hasDeleteLeadPermission,
    handleOpenEditForm,
    handleOpenViewForm,
    handleClickDelete,
  ]);

  const columnDefs = useMemo(() => {
    // if (!leadSourceList?.length || !owner?.length) return [];
    return [
      ...actionsColumn,
      {
        headerName: 'Full Name',
        field: 'fullName',
        type: "text",
        cellRenderer: (params) => {
          let cellContent;
          if (!params.value || params.node.group) {
            cellContent = params.value;
          } else if (hasInfoLeadPermission) {
            cellContent = (
              <Link
                to={`/leads/viewLead?id=${params?.data?.id}&pageNo=${gridData?.inputPayload?.pageNumber}`}
                state={params.data}
                style={{ fontWeight: '600' }}
              >
                {params.value}
              </Link>
            );
          } else {
            cellContent = <span style={{ fontWeight: '600' }}>{params.value}</span>;
          }
          return cellContent;
        },
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        minWidth: 180,
      },
      {
        headerName: 'Tags',
        field: 'tagIds',
        valueGetter: (params) => params.data.tags,
        filter: 'agSetColumnFilter',
        filterParams: filterParams([
          '(Blanks)', // Display label
          ...(tagsList && tagsList.length > 0
            ? tagsList.map((item) => item?.name?.[0]).filter(Boolean)
            : []),
        ]),
        // hide: true
      },

      {
        headerName: 'Company Name',
        field: 'companyName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        type: 'text',
        minWidth: 200,
        pinned: isMobile() ? null : 'left',
      },
      {
        headerName: 'Email',
        field: 'email',
        filter: 'agTextColumnFilter',
        type: 'text',
        cellRenderer: EmailCellRenderer,
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Phone',
        field: 'phone',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Lead Source',
        field: 'leadSource',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(leadSourceDropdown.map((item) => item.displayName)),
      },
      {
        headerName: 'Lead Owner',
        field: 'owner',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
      },
      {
        headerName: 'Campaign Name',
        field: 'campaignId',
        valueGetter: (params) => params.data?.campaignName || '',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(campaignListDropdown?.map((item) => item.campaignName)),
        // hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Industry',
        field: 'industry',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(industryTypeDropdown?.map((item) => item.displayName)),
      },
      {
        headerName: 'Business Type',
        field: 'businessType',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Line of Business',
        field: 'lineOfBusiness',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Contact LinkedIn',
        field: 'contactLinkedIn',
        type: 'text',
        filter: 'agTextColumnFilter',
        cellRenderer: (params) =>
          params.value ? (
            <a
              href={params.value.startsWith('http') ? params.value : `https://${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {params.value}
            </a>
          ) : null,
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Time Zone',
        field: 'timeZone',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(timeZoneList?.map((item) => item.displayName)),
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        valueFormatter: NameFormatter,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        minWidth: 180,
      },
      {
        headerName: 'Updated By',
        field: 'updatedBy',
        valueFormatter: NameFormatter,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        minWidth: 180,
      },
      {
        headerName: 'Created Date Time (UTC)',
        field: 'createdDateTime',
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        minWidth: 230,
        // hide: true
      },
      {
        headerName: 'Updated Date Time (UTC)',
        field: 'updatedDateTime',
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        minWidth: 230,
        // hide: true
      },
      {
        headerName: 'Company LinkedIn',
        field: 'companyLinkedIn',
        type: 'text',
        filter: 'agTextColumnFilter',
        cellRenderer: (params) => {
          if (!params.value) return null; // Return null if the value is empty or undefined

          const url = params.value.startsWith('http') ? params.value : `https://${params.value}`;
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {params.value}
            </a>
          );
        },
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Website',
        field: 'website',
        type: 'text',
        filter: 'agTextColumnFilter',
        cellRenderer: (params) => {
          if (!params.value) return null; // Return null if the value is empty or undefined

          // Ensure URL has protocol for the link
          const url = params.value.startsWith('http') ? params.value : `https://${params.value}`;

          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {params.value}
            </a>
          );
        },
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Annual Revenue',
        field: 'annualRevenue',
        type: 'text',
        filter: 'agNumberColumnFilter',
        filterParams: commonNumberFilterParams,
        valueFormatter: currencyFormatterByField('annualRevenue'),
        // hide: true,
      },
      {
        headerName: 'Written Premium',
        field: 'writtenPremium',
        type: 'text',
        filter: 'agNumberColumnFilter',
        filterParams: commonNumberFilterParams,
        valueFormatter: currencyFormatterByField('writtenPremium'),
        // hide: true,
      },
      {
        headerName: 'No. of Employees',
        field: 'noOfEmployees',
        type: 'number',
        filter: 'agNumberColumnFilter',
        filterParams: commonNumberFilterParams,
        // hide: true,
      },
      {
        headerName: 'No. of Beds',
        field: 'noOfBeds',
        type: 'number',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Source Name',
        field: 'sourceName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Other Source Name',
        field: 'otherSourceName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Salutation',
        field: 'salutation',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(salutation?.map((item) => item.displayName)),
        // hide: true,
      },
      {
        headerName: 'First Name',
        field: 'firstName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Middle Name',
        field: 'middleName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Last Name',
        field: 'lastName',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Revenue Type',
        field: 'revenueType',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(revenueTypeDropdown?.map((item) => item.displayName)),
        // hide: true,
      },
      {
        headerName: 'Title',
        field: 'title',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Secondary Email',
        field: 'secondaryEmail',
        type: 'text',
        filter: 'agTextColumnFilter',
        cellRenderer: (params) => {
          if (!params.value) return null; // Return null if the value is empty or undefined

          return <a href={`mailto:${params.value}`}>{params.value}</a>;
        },
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Mobile',
        field: 'mobile',
        type: 'number',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Direct Phone',
        field: 'directPhone',
        type: 'number',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Alternate Phone',
        field: 'alternatePhone',
        type: 'number',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Street',
        field: 'street',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'City',
        field: 'city',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Email Opt Out',
        field: 'emailOptOut',
        filter: 'agSetColumnFilter',
        type: 'checkbox',
        filterParams: checkboxFilterParams,
        // hide: true,
      },

      {
        headerName: 'State',
        field: 'state',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Zip Code',
        field: 'zipCode',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Country',
        field: 'country',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Description',
        field: 'description',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
      {
        headerName: 'Technology Details',
        field: 'technologyDetails',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // hide: true,
      },
    ];
  }, [
    hasInfoLeadPermission,
    gridData,
    actionsColumn,
    commonTextFilterParams,
    commonNumberFilterParams,
    dateFilterParams,
    checkboxFilterParams,
    filterParams,
    leadSourceDropdown,
    industryTypeDropdown,
    campaignListDropdown,
    tagsList,
    salutation,
    revenueTypeDropdown,
    timeZoneList,
    owner,
  ]);

  return columnDefs;
};

export default useLeadColumnDefs;
